import {AbstractControl, ValidationErrors, ValidatorFn} from '@angular/forms';

export const NODEMAILER_EMAIL_REGEX =
  /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+$/;

export const nodemailerEmailValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  const value: string = control.value;
  if (!value) return null;
  return NODEMAILER_EMAIL_REGEX.test(value) ? null : {nodemailerEmail: true};
};

/**
 * Returns true when the value is a non-empty, RFC-compliant email per NODEMAILER_EMAIL_REGEX.
 */
export const isValidEmail = (email: string | undefined | null): boolean => {
  if (typeof email !== 'string') return false;
  const trimmed: string = email.trim().replace(/\s+/g, '');
  if (trimmed.length === 0) return false;
  return NODEMAILER_EMAIL_REGEX.test(trimmed);
};

/**
 * Filters a list of email addresses down to trimmed, unique, RFC-compliant entries.
 * Use this at every write boundary where a recipient list will be persisted, so that
 * malformed addresses (from IQ, legacy data, or stale stored state) cannot reach Firestore.
 */
export const filterValidEmails = (emails: readonly string[] | undefined | null): string[] => {
  if (!emails || emails.length === 0) return [];
  const seen: Set<string> = new Set<string>();
  const result: string[] = [];
  for (const email of emails) {
    if (typeof email !== 'string') continue;
    const trimmed: string = email.trim().replace(/\s+/g, '');
    if (trimmed.length === 0) continue;
    if (!NODEMAILER_EMAIL_REGEX.test(trimmed)) continue;
    if (seen.has(trimmed)) continue;
    seen.add(trimmed);
    result.push(trimmed);
  }
  return result;
};

/**
 * Returns the subset of the input list that is NOT a valid email address.
 * Useful for surfacing dropped entries to the user or logs without changing input order.
 */
export const findInvalidEmails = (emails: readonly string[] | undefined | null): string[] => {
  if (!emails || emails.length === 0) return [];
  return emails.filter((email: string): boolean => !isValidEmail(email));
};
